import { Formik, FormikHelpers, FormikProps } from 'formik';
import { RefObject, useEffect, useRef } from 'react';

import { FormValues } from 'theSource/types';
import { DirtyFormPrompt, InnerForm } from './components';

export type ResetFormHandler = (cb?: () => void) => void;

export interface FormProps<T extends object> {
  children: JSX.Element | JSX.Element[];
  childrenStackSpacing?: number;
  onCancelClick?: () => void;
  onSubmitClick: (values: T, resetForm: ResetFormHandler) => void;
  initialValues: T;
  isLoading: boolean;
  testId?: string;
  validationSchema?: any | (() => any);
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  submitButtonLabel?: string;
  submitButtonUseFullWidth?: boolean;
  formRef?: RefObject<FormikProps<T>>;
  onChange?: (values: FormValues) => void;
  enableDirtyFormHandling?: boolean;
  disableSubmitIfInvalid?: boolean;
  disableSubmitButton?: boolean;
}

export const Form = <T extends object>({
  children,
  initialValues,
  isLoading,
  onSubmitClick,
  validationSchema,
  validateOnChange,
  validateOnBlur,
  formRef,
  enableDirtyFormHandling = true,
  ...formProps
}: FormProps<T>) => {
  const innerFormRef = useRef<FormikProps<T>>(null);
  const ref = formRef || innerFormRef;

  useEffect(() => {
    ref.current?.setSubmitting(isLoading);
  }, [isLoading, ref]);

  const handleSubmit = (values: T, formikHelpers: FormikHelpers<T>) =>
    onSubmitClick(values, () => formikHelpers.resetForm());

  useEffect(() => {
    ref.current?.validateForm();
  }, [ref, validationSchema]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={validateOnChange}
      validateOnBlur={validateOnBlur}
      innerRef={ref}
    >
      {(props) => (
        <>
          <InnerForm {...formProps} formikProps={props} isLoading={isLoading}>
            {children}
          </InnerForm>
          {enableDirtyFormHandling && <DirtyFormPrompt isDirty={props.dirty} />}
        </>
      )}
    </Formik>
  );
};
